import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleQuery, useListVehicleAlbumsQuery} from '@dms/api/sales';
import {EntityResourceIds, VehicleAlbumResponseBody, VehicleAlbums} from '@dms/api/shared';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {VehicleAlbum} from './VehicleAlbum';
import {VehicleCreateAlbumButton} from './VehicleCreateAlbumButton';

interface VehicleAlbumGalleryProps extends RequiredTestIdProps {
  vehicleId: string;
  serviceCaseId?: string;
  enableCreate?: boolean;
  excludeServiceCaseAlbums?: boolean;
  hideEmptyServiceCaseAlbums?: boolean;
  onAlbumChange?: () => void;
}

// Sale-cycle album names auto-created on every vehicle. Hidden from the service-vehicle /
// service-case gallery since they're only meaningful in the sale-vehicle flow.
const SALE_ALBUM_NAMES = new Set([
  'PHOTOS_CAR_SALES',
  'PHOTOS_CAR_PURCHASE',
  'PHOTOS_CAR_ORIGINAL',
]);

export function VehicleAlbumGallery(props: VehicleAlbumGalleryProps) {
  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});

  const [canCreateVehicleAlbum] = usePermissions({
    permissionKeys: ['createVehicleAlbumPhotos'],
    scopes: {
      createVehicleAlbumPhotos: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const {
    data: albums,
    isLoading,
    isError,
  } = useListVehicleAlbumsQuery({
    vehicleId: props.vehicleId,
    serviceCaseId: props.serviceCaseId,
  });

  const visibleAlbums =
    albums?.filter((album) => {
      if (SALE_ALBUM_NAMES.has(album.name)) {
        return false;
      }

      if (
        props.excludeServiceCaseAlbums &&
        (isNotNil(album.serviceCaseId) || album.name === VehicleAlbums.PHOTOS_SERVICE_CASE)
      ) {
        return false;
      }

      if (isNotNil(props.serviceCaseId)) {
        return album.serviceCaseId === props.serviceCaseId;
      }

      return true;
    }) ?? [];

  return (
    <VStack spacing={4}>
      <Show when={props.enableCreate && canCreateVehicleAlbum}>
        <HStack justify="flex-end">
          <VehicleCreateAlbumButton
            data-testid={suffixTestId('vehicleAlbumGallery-create', props)}
            vehicleId={props.vehicleId}
            serviceCaseId={props.serviceCaseId}
            onComplete={props.onAlbumChange}
          />
        </HStack>
      </Show>
      <DataStatus isLoading={isLoading} isError={isError} isEmpty={visibleAlbums.length === 0}>
        <VStack spacing={4}>
          {visibleAlbums.map((album) => {
            const canDelete =
              album.name === VehicleAlbums.PHOTOS_CUSTOM ||
              album.name === VehicleAlbums.PHOTOS_SERVICE_CASE;
            const testId = suffixTestId(`vehicleAlbumGallery-album-${album.id}`, props);

            if (
              album.name === VehicleAlbums.PHOTOS_SERVICE_CASE &&
              isNotNil(album.serviceCaseId) &&
              !isNotNil(props.serviceCaseId)
            ) {
              return (
                <ServiceCaseVehicleAlbum
                  key={album.id}
                  data-testid={testId}
                  album={album}
                  vehicleId={props.vehicleId}
                  canDelete={canDelete}
                  hideWhenEmpty={props.hideEmptyServiceCaseAlbums}
                  onAlbumChange={props.onAlbumChange}
                />
              );
            }

            return (
              <VehicleAlbum
                key={album.id}
                data-testid={testId}
                vehicleId={props.vehicleId}
                vehicleAlbumId={album.id}
                title={album.translation}
                canUpload
                canDelete={canDelete}
                hideWhenEmpty={
                  props.hideEmptyServiceCaseAlbums &&
                  album.name === VehicleAlbums.PHOTOS_SERVICE_CASE
                }
                onAlbumChange={props.onAlbumChange}
              />
            );
          })}
        </VStack>
      </DataStatus>
    </VStack>
  );
}

interface ServiceCaseVehicleAlbumProps extends RequiredTestIdProps {
  album: VehicleAlbumResponseBody;
  vehicleId: string;
  canDelete: boolean;
  hideWhenEmpty?: boolean;
  onAlbumChange?: () => void;
}

function ServiceCaseVehicleAlbum(props: ServiceCaseVehicleAlbumProps) {
  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    isNotNil(props.album.serviceCaseId) ? {serviceCaseId: props.album.serviceCaseId} : skipToken
  );

  const serviceCaseNumber = serviceCase?.number;
  const title = isNotNil(serviceCaseNumber)
    ? `${props.album.translation} · ${serviceCaseNumber}`
    : props.album.translation;

  return (
    <VehicleAlbum
      data-testid={props['data-testid']}
      vehicleId={props.vehicleId}
      vehicleAlbumId={props.album.id}
      title={title}
      canUpload
      canDelete={props.canDelete}
      hideWhenEmpty={props.hideWhenEmpty}
      onAlbumChange={props.onAlbumChange}
    />
  );
}
